import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import {
  PHASES,
  GENDER_QUESTION,
  AGE_QUESTION,
  LIFE_STAGE_QUESTION,
} from "../../data/assessment";
import { MALE_QUESTIONS } from "../../data/male_assessment";
import { refreshQuestionOverrides } from "../../hooks/useQuestionOverrides";
import { ChevronDown, ChevronRight, Search, RotateCcw } from "lucide-react";

// Enumerate every editable question with default EN + IT text.
function buildCatalog() {
  const items = [];
  const push = (group, q) => {
    items.push({
      group,
      qId: q.id,
      defaultTitle:  { en: q.title?.en  || "", it: q.title?.it  || "" },
      defaultHelper: q.helper ? { en: q.helper.en || "", it: q.helper.it || "" } : null,
      defaultBridge: q.bridge ? { en: q.bridge.en || "", it: q.bridge.it || "" } : null,
      options: (q.options || []).map((o) => ({
        oId: o.id,
        defaultLabel: { en: o.label?.en || "", it: o.label?.it || "" },
      })),
    });
  };
  push("Woman path — start screens", GENDER_QUESTION);
  push("Woman path — start screens", AGE_QUESTION);
  push("Woman path — start screens", LIFE_STAGE_QUESTION);
  PHASES.forEach((p) => {
    p.questions.forEach((q) => push(`Woman path — ${p.title.en}`, q));
  });
  MALE_QUESTIONS.forEach((q) => push(`Man path`, q));
  return items;
}

// Enumerate every editable phase (section label + interstitial text).
function buildPhaseCatalog() {
  return PHASES.map((p) => ({
    pId: p.id,
    defaultTitle:        { en: p.title?.en         || "", it: p.title?.it         || "" },
    defaultInsightTitle: { en: p.insight?.title?.en || "", it: p.insight?.title?.it || "" },
    defaultInsightBody:  { en: p.insight?.body?.en  || "", it: p.insight?.body?.it  || "" },
  }));
}

function QuestionEditor({ item, override, onChange, onReset }) {
  const t = (l) => override?.title?.[l] ?? "";
  const h = (l) => override?.helper?.[l] ?? "";
  const b = (l) => override?.bridge?.[l] ?? "";
  const opt = (oId, l) => override?.options?.[oId]?.label?.[l] ?? "";

  const setTitle  = (l, v) => onChange({ ...override, title:  { ...(override?.title  || {}), [l]: v } });
  const setHelper = (l, v) => onChange({ ...override, helper: { ...(override?.helper || {}), [l]: v } });
  const setBridge = (l, v) => onChange({ ...override, bridge: { ...(override?.bridge || {}), [l]: v } });
  const setLabel = (oId, l, v) => onChange({
    ...override,
    options: {
      ...(override?.options || {}),
      [oId]: {
        label: {
          ...(override?.options?.[oId]?.label || {}),
          [l]: v,
        },
      },
    },
  });

  return (
    <div className="border border-[#E5E5E5] rounded-2xl bg-white p-4 sm:p-5 space-y-4" data-testid={`q-editor-${item.qId}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Question</div>
          <div className="text-[11px] font-mono text-[#A8AD94] truncate">{item.qId}</div>
        </div>
        {override && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 text-[11px] uppercase kt-tracking text-[#6F6B66] hover:text-[#383633]"
            data-testid={`reset-${item.qId}`}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset all
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">Title · EN</label>
          <textarea
            rows={2}
            value={t("en")}
            onChange={(e) => setTitle("en", e.target.value)}
            placeholder={item.defaultTitle.en}
            className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
            data-testid={`title-en-${item.qId}`}
          />
          <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {item.defaultTitle.en}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">Title · IT</label>
          <textarea
            rows={2}
            value={t("it")}
            onChange={(e) => setTitle("it", e.target.value)}
            placeholder={item.defaultTitle.it}
            className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
            data-testid={`title-it-${item.qId}`}
          />
          <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {item.defaultTitle.it}</div>
        </div>
      </div>

      {item.defaultHelper && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">Helper / subtitle · EN</label>
            <textarea
              rows={2}
              value={h("en")}
              onChange={(e) => setHelper("en", e.target.value)}
              placeholder={item.defaultHelper.en}
              className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
              data-testid={`helper-en-${item.qId}`}
            />
            <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {item.defaultHelper.en}</div>
          </div>
          <div>
            <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">Helper / subtitle · IT</label>
            <textarea
              rows={2}
              value={h("it")}
              onChange={(e) => setHelper("it", e.target.value)}
              placeholder={item.defaultHelper.it}
              className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
              data-testid={`helper-it-${item.qId}`}
            />
            <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {item.defaultHelper.it}</div>
          </div>
        </div>
      )}

      {item.defaultBridge && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">Bridge text · EN</label>
            <textarea
              rows={2}
              value={b("en")}
              onChange={(e) => setBridge("en", e.target.value)}
              placeholder={item.defaultBridge.en}
              className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
              data-testid={`bridge-en-${item.qId}`}
            />
            <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {item.defaultBridge.en}</div>
          </div>
          <div>
            <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">Bridge text · IT</label>
            <textarea
              rows={2}
              value={b("it")}
              onChange={(e) => setBridge("it", e.target.value)}
              placeholder={item.defaultBridge.it}
              className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
              data-testid={`bridge-it-${item.qId}`}
            />
            <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {item.defaultBridge.it}</div>
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-[#E5E5E5]">
        <div className="text-[10px] uppercase kt-tracking text-[#6F6B66] mb-2">Options</div>
        <div className="space-y-3">
          {item.options.map((o) => (
            <div key={o.oId} className="grid sm:grid-cols-[100px,1fr,1fr] gap-2 items-start">
              <div className="text-[11px] font-mono text-[#6F6B66] pt-2 truncate">{o.oId}</div>
              <input
                value={opt(o.oId, "en")}
                onChange={(e) => setLabel(o.oId, "en", e.target.value)}
                placeholder={o.defaultLabel.en}
                className="w-full text-sm rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#383633]"
                data-testid={`opt-en-${item.qId}-${o.oId}`}
              />
              <input
                value={opt(o.oId, "it")}
                onChange={(e) => setLabel(o.oId, "it", e.target.value)}
                placeholder={o.defaultLabel.it}
                className="w-full text-sm rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#383633]"
                data-testid={`opt-it-${item.qId}-${o.oId}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhaseEditor({ item, override, onChange, onReset }) {
  const val = (field, lang) => override?.[field]?.[lang] ?? "";
  const setField = (field, lang, v) => onChange({
    ...override,
    [field]: { ...(override?.[field] || {}), [lang]: v },
  });

  const row = (field, defaults, label) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">{label} · EN</label>
        <textarea
          rows={field === "insight_body" ? 3 : 2}
          value={val(field, "en")}
          onChange={(e) => setField(field, "en", e.target.value)}
          placeholder={defaults.en}
          className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
          data-testid={`phase-${item.pId}-${field}-en`}
        />
        <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {defaults.en}</div>
      </div>
      <div>
        <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">{label} · IT</label>
        <textarea
          rows={field === "insight_body" ? 3 : 2}
          value={val(field, "it")}
          onChange={(e) => setField(field, "it", e.target.value)}
          placeholder={defaults.it}
          className="mt-1 w-full text-sm rounded-xl border border-[#E5E5E5] bg-white p-3 focus:outline-none focus:ring-2 focus:ring-[#383633]"
          data-testid={`phase-${item.pId}-${field}-it`}
        />
        <div className="text-[10px] text-[#A8AD94] mt-1 truncate">Default: {defaults.it}</div>
      </div>
    </div>
  );

  return (
    <div className="border border-[#E5E5E5] rounded-2xl bg-white p-4 sm:p-5 space-y-4" data-testid={`p-editor-${item.pId}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Phase / section</div>
          <div className="text-[11px] font-mono text-[#A8AD94] truncate">{item.pId}</div>
        </div>
        {override && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 text-[11px] uppercase kt-tracking text-[#6F6B66] hover:text-[#383633]"
            data-testid={`reset-phase-${item.pId}`}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset all
          </button>
        )}
      </div>
      {row("title",         item.defaultTitle,        "Section title (small label)")}
      {row("insight_title", item.defaultInsightTitle, "Interstitial headline")}
      {row("insight_body",  item.defaultInsightBody,  "Interstitial paragraph")}
    </div>
  );
}

export default function AdminQuestions() {
  const catalog = useMemo(buildCatalog, []);
  const phaseCatalog = useMemo(buildPhaseCatalog, []);
  const [overrides, setOverrides] = useState({});
  const [phases, setPhases] = useState({});
  const [labels, setLabels] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [phasesOpen, setPhasesOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/questions/overrides`);
      setOverrides(r.data?.map || {});
      setPhases(r.data?.phases || {});
      setLabels(r.data?.labels || {});
      setDirty(false);
    } catch {
      toast.error("Could not load question overrides");
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const filter = q.trim().toLowerCase();
    const b = {};
    catalog.forEach((it) => {
      if (filter) {
        const hay = `${it.group} ${it.qId} ${it.defaultTitle.en} ${it.defaultTitle.it}`.toLowerCase();
        if (!hay.includes(filter)) return;
      }
      (b[it.group] = b[it.group] || []).push(it);
    });
    return b;
  }, [catalog, q]);

  useEffect(() => {
    if (q.trim()) {
      const next = {};
      Object.keys(grouped).forEach((g) => { next[g] = true; });
      setOpenGroups(next);
    }
  }, [q, grouped]);

  const updateItem = (qId, next) => {
    setOverrides((prev) => {
      const n = { ...prev };
      // Prune empties so we don't save {"title": {"en": ""}} — treat empty as "use default"
      const cleaned = {};
      const cleanLang = (obj) => {
        if (!obj) return null;
        const out = {};
        Object.entries(obj).forEach(([l, v]) => { if (v && v.trim()) out[l] = v.trim(); });
        return Object.keys(out).length ? out : null;
      };
      const cTitle  = cleanLang(next?.title);   if (cTitle)  cleaned.title  = cTitle;
      const cHelper = cleanLang(next?.helper);  if (cHelper) cleaned.helper = cHelper;
      const cBridge = cleanLang(next?.bridge);  if (cBridge) cleaned.bridge = cBridge;
      if (next?.options) {
        const o = {};
        Object.entries(next.options).forEach(([oid, val]) => {
          const l = cleanLang(val?.label);
          if (l) o[oid] = { label: l };
        });
        if (Object.keys(o).length) cleaned.options = o;
      }
      if (Object.keys(cleaned).length) n[qId] = cleaned;
      else delete n[qId];
      return n;
    });
    setDirty(true);
  };

  const updatePhase = (pId, next) => {
    setPhases((prev) => {
      const n = { ...prev };
      const cleaned = {};
      const cleanLang = (obj) => {
        if (!obj) return null;
        const out = {};
        Object.entries(obj).forEach(([l, v]) => { if (v && v.trim()) out[l] = v.trim(); });
        return Object.keys(out).length ? out : null;
      };
      ["title", "insight_title", "insight_body"].forEach((f) => {
        const c = cleanLang(next?.[f]);
        if (c) cleaned[f] = c;
      });
      if (Object.keys(cleaned).length) n[pId] = cleaned;
      else delete n[pId];
      return n;
    });
    setDirty(true);
  };

  const resetItem = async (qId) => {
    try {
      await axios.delete(`${API}/admin/questions/overrides/${encodeURIComponent(qId)}`);
      await load();
      await refreshQuestionOverrides();
      toast.success("Reset to default");
    } catch {
      toast.error("Could not reset");
    }
  };
  const resetPhase = async (pId) => {
    try {
      await axios.delete(`${API}/admin/questions/overrides/${encodeURIComponent("phase:" + pId)}`);
      await load();
      await refreshQuestionOverrides();
      toast.success("Reset to default");
    } catch {
      toast.error("Could not reset");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/questions/overrides`, { map: overrides, phases, labels });
      await refreshQuestionOverrides();
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setLabelField = (key, lang, v) => {
    setLabels((prev) => {
      const n = { ...prev };
      const entry = { ...(n[key] || {}) };
      if (v && v.trim()) entry[lang] = v.trim();
      else delete entry[lang];
      if (Object.keys(entry).length) n[key] = entry;
      else delete n[key];
      return n;
    });
    setDirty(true);
  };

  const total = catalog.length;
  const customized = Object.keys(overrides).length;
  const phasesCustomized = Object.keys(phases).length;
  const labelsCustomized = Object.keys(labels).length;

  // Shared UI labels the admin can edit — small tight whitelist.
  // Defaults are surfaced as placeholders so the admin sees the current copy.
  const UI_LABELS = [
    { key: "continue", en: "Continue", it: "Continua", help: "Interstitial screens (between phases)" },
    { key: "next",     en: "Next",     it: "Avanti",   help: "Any other 'Next' button in the flow" },
    { key: "prev",     en: "Back",     it: "Indietro", help: "Any 'Back' link in the flow" },
  ];

  return (
    <div className="max-w-4xl" data-testid="admin-questions-page">
      <div className="mb-8">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">Admin</div>
        <h1 className="text-3xl text-[#383633] tracking-tight font-medium">Questions Text</h1>
        <p className="text-sm text-[#6F6B66] mt-2 max-w-2xl">
          Edit any question title or option label in EN + IT. Leave a field blank to use the default text. Click "Save changes" to publish — no redeploy needed.
        </p>
        <div className="mt-4 text-xs text-[#A8AD94]">
          {customized} of {total} questions customized · {phasesCustomized} of {phaseCatalog.length} phases customized · {labelsCustomized} of {UI_LABELS.length} shared labels customized
        </div>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6B66]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by question id, title…"
            data-testid="search-input"
            className="w-full pl-9 pr-4 py-2 rounded-full border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#383633]"
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="save-btn"
          className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white px-6 py-2 text-xs uppercase kt-tracking disabled:opacity-50"
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {/* ── Shared UI labels (button text used across the assessment) ── */}
      <div className="mb-4 border border-[#E5E5E5] rounded-2xl bg-[#F3F3F3] overflow-hidden" data-testid="labels-group">
        <button
          type="button"
          onClick={() => setLabelsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/50"
          data-testid="labels-group-toggle"
        >
          <div>
            <div className="text-sm text-[#383633] tracking-tight">Shared button labels</div>
            <div className="text-[10px] text-[#6F6B66] uppercase kt-tracking mt-0.5">
              {UI_LABELS.length} labels · {labelsCustomized} customized
            </div>
          </div>
          {labelsOpen ? <ChevronDown className="h-4 w-4 text-[#6F6B66]" /> : <ChevronRight className="h-4 w-4 text-[#6F6B66]" />}
        </button>
        {labelsOpen && (
          <div className="p-3 space-y-3 bg-white/40">
            {UI_LABELS.map((lbl) => (
              <div key={lbl.key} className="border border-[#E5E5E5] rounded-2xl bg-white p-4 sm:p-5 space-y-3" data-testid={`ui-label-${lbl.key}`}>
                <div>
                  <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{lbl.key} label</div>
                  <div className="text-[11px] text-[#6F6B66] mt-0.5">{lbl.help}</div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">EN</label>
                    <input
                      value={labels[lbl.key]?.en ?? ""}
                      onChange={(e) => setLabelField(lbl.key, "en", e.target.value)}
                      placeholder={lbl.en}
                      className="mt-1 w-full text-sm rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#383633]"
                      data-testid={`label-en-${lbl.key}`}
                    />
                    <div className="text-[10px] text-[#A8AD94] mt-1">Default: {lbl.en}</div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase kt-tracking text-[#6F6B66]">IT</label>
                    <input
                      value={labels[lbl.key]?.it ?? ""}
                      onChange={(e) => setLabelField(lbl.key, "it", e.target.value)}
                      placeholder={lbl.it}
                      className="mt-1 w-full text-sm rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#383633]"
                      data-testid={`label-it-${lbl.key}`}
                    />
                    <div className="text-[10px] text-[#A8AD94] mt-1">Default: {lbl.it}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Phase / section text (interstitials) ── */}
      <div className="mb-4 border border-[#E5E5E5] rounded-2xl bg-[#F3F3F3] overflow-hidden" data-testid="phase-group">
        <button
          type="button"
          onClick={() => setPhasesOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/50"
          data-testid="phase-group-toggle"
        >
          <div>
            <div className="text-sm text-[#383633] tracking-tight">Section & interstitial text</div>
            <div className="text-[10px] text-[#6F6B66] uppercase kt-tracking mt-0.5">
              {phaseCatalog.length} sections · {phasesCustomized} customized
            </div>
          </div>
          {phasesOpen ? <ChevronDown className="h-4 w-4 text-[#6F6B66]" /> : <ChevronRight className="h-4 w-4 text-[#6F6B66]" />}
        </button>
        {phasesOpen && (
          <div className="p-3 space-y-3 bg-white/40">
            {phaseCatalog.map((p) => (
              <PhaseEditor
                key={p.pId}
                item={p}
                override={phases[p.pId]}
                onChange={(n) => updatePhase(p.pId, n)}
                onReset={() => resetPhase(p.pId)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {Object.entries(grouped).map(([g, items]) => {
          const open = openGroups[g] ?? false;
          const custom = items.filter((i) => overrides[i.qId]).length;
          return (
            <div key={g} className="border border-[#E5E5E5] rounded-2xl bg-[#F3F3F3] overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenGroups((o) => ({ ...o, [g]: !o[g] }))}
                data-testid={`group-toggle-${g.replace(/\s/g, "-")}`}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/50"
              >
                <div>
                  <div className="text-sm text-[#383633] tracking-tight">{g}</div>
                  <div className="text-[10px] text-[#6F6B66] uppercase kt-tracking mt-0.5">
                    {items.length} questions · {custom} customized
                  </div>
                </div>
                {open ? <ChevronDown className="h-4 w-4 text-[#6F6B66]" /> : <ChevronRight className="h-4 w-4 text-[#6F6B66]" />}
              </button>
              {open && (
                <div className="p-3 space-y-3 bg-white/40">
                  {items.map((it) => (
                    <QuestionEditor
                      key={it.qId}
                      item={it}
                      override={overrides[it.qId]}
                      onChange={(n) => updateItem(it.qId, n)}
                      onReset={() => resetItem(it.qId)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {Object.keys(grouped).length === 0 && (
          <div className="text-sm text-[#6F6B66] text-center py-8">No questions match "{q}"</div>
        )}
      </div>
    </div>
  );
}
